import { useState, useEffect, useMemo, useRef } from 'react';
import { Modal, Form, Button, Alert } from 'react-bootstrap';
import { Search, X, ChevronDown } from 'lucide-react';
import type {
  AgentPersonaResponse,
  UpsertAgentPersonaRequest,
  RegistryTreeResponse,
} from '../../../infrastructure/AIAgentRegistryClient';

interface UpsertAgentModalProps {
  show: boolean;
  agent: AgentPersonaResponse | null;
  type: 'supervisor' | 'micro';
  parentId?: string | undefined;
  registryTree?: RegistryTreeResponse | undefined;
  onHide: () => void;
  onSubmit: (data: UpsertAgentPersonaRequest) => void;
  isSubmitting: boolean;
}

export const UpsertAgentModal = ({
  show,
  agent,
  type,
  parentId,
  registryTree,
  onHide,
  onSubmit,
  isSubmitting,
}: UpsertAgentModalProps) => {
  const [pageId, setPageId] = useState('');
  const [pageSearch, setPageSearch] = useState('');
  const [isPageDropdownOpen, setIsPageDropdownOpen] = useState(false);
  const [name, setName] = useState('');
  const [description, setDescription] = useState('');
  const [manualId, setManualId] = useState('');
  const [manualSearch, setManualSearch] = useState('');
  const [isManualDropdownOpen, setIsManualDropdownOpen] = useState(false);
  const [requiredParams, setRequiredParams] = useState('');
  const [errors, setErrors] = useState<Record<string, string>>({});
  const pageDropdownRef = useRef<HTMLDivElement>(null);
  const manualDropdownRef = useRef<HTMLDivElement>(null);

  const isEdit = !!agent;

  useEffect(() => {
    if (agent) {
      setPageId(agent.pageId);
      setName(agent.name);
      setDescription(agent.description);
      setManualId(agent.manualId || '');
      setRequiredParams('');
    } else {
      setPageId('');
      setName('');
      setDescription('');
      setManualId('');
      setRequiredParams('');
    }
    setPageSearch('');
    setIsPageDropdownOpen(false);
    setManualSearch('');
    setIsManualDropdownOpen(false);
    setErrors({});
  }, [agent, show]);

  // Close dropdowns when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (pageDropdownRef.current && !pageDropdownRef.current.contains(event.target as Node)) {
        setIsPageDropdownOpen(false);
      }
      if (manualDropdownRef.current && !manualDropdownRef.current.contains(event.target as Node)) {
        setIsManualDropdownOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const personaPages = registryTree?.personas || [];
  const filteredPages = useMemo(() => {
    if (!pageSearch.trim()) return personaPages;
    const searchLower = pageSearch.toLowerCase();
    return personaPages.filter((page) => page.title.toLowerCase().includes(searchLower));
  }, [personaPages, pageSearch]);

  const selectedPageTitle = useMemo(() => {
    if (!pageId) return '';
    const found = personaPages.find((p) => p.id === pageId);
    return found?.title || '';
  }, [pageId, personaPages]);

  const manualPages = registryTree?.manuals || [];
  const filteredManuals = useMemo(() => {
    if (!manualSearch.trim()) return manualPages;
    const searchLower = manualSearch.toLowerCase();
    return manualPages.filter((manual) => manual.title.toLowerCase().includes(searchLower));
  }, [manualPages, manualSearch]);

  const selectedManualTitle = useMemo(() => {
    if (!manualId) return '';
    const found = manualPages.find((m) => m.id === manualId);
    return found?.title || '';
  }, [manualId, manualPages]);

  const validate = (): boolean => {
    const newErrors: Record<string, string> = {};
    if (!pageId.trim()) newErrors['pageId'] = 'Notion Page ID is required';
    if (!name.trim()) newErrors['name'] = 'Name is required';
    if (!description.trim()) newErrors['description'] = 'Description is required';
    if (type === 'micro' && !manualId.trim()) newErrors['manualId'] = 'Linked Manual is required';
    if (requiredParams.trim()) {
      try {
        JSON.parse(requiredParams);
      } catch {
        newErrors['requiredParams'] = 'Invalid JSON format';
      }
    }
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (!validate()) return;

    const data: UpsertAgentPersonaRequest = {
      pageId,
      name,
      type,
      description,
    };

    if (isEdit && agent) data.id = agent.id;
    if (type === 'micro' && parentId) data.parentId = parentId;
    if (manualId.trim()) data.manualId = manualId;
    if (requiredParams.trim()) data.requiredParams = JSON.parse(requiredParams);

    onSubmit(data);
  };

  return (
    <Modal show={show} onHide={onHide} size="lg">
      <Modal.Header closeButton>
        <Modal.Title>{isEdit ? 'Edit' : 'Create'} {type === 'supervisor' ? 'Supervisor' : 'Micro'} Agent</Modal.Title>
      </Modal.Header>
      <Form onSubmit={handleSubmit}>
        <Modal.Body>
          <Form.Group className="mb-3">
            <Form.Label>Notion Page ID *</Form.Label>
            {personaPages.length > 0 ? (
              <div ref={pageDropdownRef} style={{ position: 'relative' }}>
                <div 
                  className={`form-control d-flex align-items-center justify-content-between ${errors['pageId'] ? 'is-invalid' : ''}`}
                  style={{ cursor: 'pointer', minHeight: '38px' }}
                  onClick={() => setIsPageDropdownOpen(!isPageDropdownOpen)}
                >
                  {pageId ? (
                    <span>{selectedPageTitle || pageId}</span>
                  ) : (
                    <span className="text-muted">Select a Notion page...</span>
                  )}
                  <ChevronDown size={16} className="text-muted" />
                </div>
                
                {isPageDropdownOpen && (
                  <div 
                    className="border rounded bg-white shadow-sm"
                    style={{ 
                      position: 'absolute', 
                      top: '100%', 
                      left: 0, 
                      right: 0, 
                      zIndex: 1050,
                      maxHeight: '250px',
                      overflowY: 'auto'
                    }}
                  >
                    <div className="p-2 border-bottom sticky-top bg-white">
                      <div className="input-group input-group-sm">
                        <span className="input-group-text bg-light"><Search size={14} /></span>
                        <input
                          type="text"
                          className="form-control"
                          value={pageSearch}
                          onChange={(e) => setPageSearch(e.target.value)}
                          placeholder="Type to search..."
                          autoFocus
                          onClick={(e) => e.stopPropagation()}
                        />
                      </div>
                    </div>
                    
                    {filteredPages.length > 0 ? (
                      filteredPages.map((page) => (
                        <div
                          key={page.id}
                          className={`px-3 py-2 ${pageId === page.id ? 'bg-primary text-white' : ''}`}
                          style={{ cursor: 'pointer' }}
                          onClick={() => {
                            setPageId(page.id);
                            if (!name.trim()) setName(page.title);
                            setPageSearch('');
                            setIsPageDropdownOpen(false);
                          }}
                          onMouseEnter={(e) => {
                            if (pageId !== page.id) {
                              e.currentTarget.style.backgroundColor = '#f8f9fa';
                            }
                          }}
                          onMouseLeave={(e) => {
                            if (pageId !== page.id) {
                              e.currentTarget.style.backgroundColor = '';
                            }
                          }}
                        >
                          {page.title}
                        </div>
                      ))
                    ) : (
                      <div className="px-3 py-2 text-muted fst-italic">
                        {pageSearch ? 'No pages match your search' : 'No pages available'}
                      </div>
                    )}
                  </div>
                )}
                {errors['pageId'] && <div className="invalid-feedback d-block">{errors['pageId']}</div>}
              </div>
            ) : (
              <>
                <Form.Control
                  type="text"
                  value={pageId}
                  onChange={(e) => setPageId(e.target.value)}
                  placeholder="Enter Notion page ID"
                  isInvalid={!!errors['pageId']}
                />
                <Form.Control.Feedback type="invalid">{errors['pageId']}</Form.Control.Feedback>
              </>
            )}
          </Form.Group>

          <Form.Group className="mb-3">
            <Form.Label>Name *</Form.Label>
            <Form.Control
              type="text"
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder="e.g., OrderCancelAgent"
              isInvalid={!!errors['name']}
            />
            <Form.Control.Feedback type="invalid">{errors['name']}</Form.Control.Feedback>
          </Form.Group>

          <Form.Group className="mb-3">
            <Form.Label>Description *</Form.Label>
            <Form.Control
              as="textarea"
              rows={3}
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              placeholder="Describe what this agent does..."
              isInvalid={!!errors['description']}
            />
            <Form.Control.Feedback type="invalid">{errors['description']}</Form.Control.Feedback>
          </Form.Group>

          {type === 'micro' && (
            <>
              <Form.Group className="mb-3">
                <Form.Label>Linked Manual *</Form.Label>
                <div ref={manualDropdownRef} style={{ position: 'relative' }}>
                  <div 
                    className={`form-control d-flex align-items-center justify-content-between ${errors['manualId'] ? 'is-invalid' : ''}`}
                    style={{ cursor: 'pointer', minHeight: '38px' }}
                    onClick={() => setIsManualDropdownOpen(!isManualDropdownOpen)}
                  >
                    {manualId ? (
                      <div className="d-flex align-items-center gap-2 flex-grow-1">
                        <span className="badge bg-primary">{selectedManualTitle || manualId}</span>
                        <Button
                          variant="link"
                          size="sm"
                          className="p-0 text-danger ms-auto"
                          onClick={(e) => {
                            e.stopPropagation();
                            setManualId('');
                            setManualSearch('');
                          }}
                        >
                          <X size={14} />
                        </Button>
                      </div>
                    ) : (
                      <span className="text-muted">Select a manual...</span>
                    )}
                    <ChevronDown size={16} className="text-muted" />
                  </div>
                  
                  {isManualDropdownOpen && (
                    <div 
                      className="border rounded bg-white shadow-sm"
                      style={{ 
                        position: 'absolute', 
                        top: '100%', 
                        left: 0, 
                        right: 0, 
                        zIndex: 1050,
                        maxHeight: '250px',
                        overflowY: 'auto'
                      }}
                    >
                      <div className="p-2 border-bottom sticky-top bg-white">
                        <div className="input-group input-group-sm">
                          <span className="input-group-text bg-light"><Search size={14} /></span>
                          <input
                            type="text"
                            className="form-control"
                            value={manualSearch}
                            onChange={(e) => setManualSearch(e.target.value)}
                            placeholder="Type to search..."
                            autoFocus
                            onClick={(e) => e.stopPropagation()}
                          />
                        </div>
                      </div>
                      
                      {filteredManuals.length > 0 ? (
                        filteredManuals.map((manual) => (
                          <div
                            key={manual.id}
                            className={`px-3 py-2 ${manualId === manual.id ? 'bg-primary text-white' : ''}`}
                            style={{ cursor: 'pointer' }}
                            onClick={() => {
                              setManualId(manual.id);
                              setManualSearch('');
                              setIsManualDropdownOpen(false);
                            }}
                            onMouseEnter={(e) => {
                              if (manualId !== manual.id) {
                                e.currentTarget.style.backgroundColor = '#f8f9fa';
                              }
                            }}
                            onMouseLeave={(e) => {
                              if (manualId !== manual.id) {
                                e.currentTarget.style.backgroundColor = '';
                              }
                            }}
                          >
                            {manual.title}
                          </div>
                        ))
                      ) : (
                        <div className="px-3 py-2 text-muted fst-italic">
                          {manualSearch ? 'No manuals match your search' : 'No manuals available'}
                        </div>
                      )}
                    </div>
                  )}
                  {errors['manualId'] && <div className="invalid-feedback d-block">{errors['manualId']}</div>}
                </div>
              </Form.Group>

              <Form.Group className="mb-3">
                <Form.Label>Required Parameters (JSON Schema, optional)</Form.Label>
                <Form.Control
                  as="textarea"
                  rows={5}
                  value={requiredParams}
                  onChange={(e) => setRequiredParams(e.target.value)}
                  placeholder='{"type": "object", "required": ["orderId"], "properties": {...}}'
                  isInvalid={!!errors['requiredParams']}
                  style={{ fontFamily: 'monospace', fontSize: '0.85rem' }}
                />
                <Form.Control.Feedback type="invalid">{errors['requiredParams']}</Form.Control.Feedback>
              </Form.Group>
            </>
          )}

          {type === 'micro' && parentId && (
            <Alert variant="info">This micro agent will be created under supervisor: {parentId}</Alert>
          )}
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={onHide}>Cancel</Button>
          <Button variant="primary" type="submit" disabled={isSubmitting}>
            {isSubmitting ? 'Saving...' : isEdit ? 'Update' : 'Create'}
          </Button>
        </Modal.Footer>
      </Form>
    </Modal>
  );
};

